import React, { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Loader2, Mail } from "lucide-react";
import { authClient, platformClient, setPlatformToken } from "@/lib/musicApi";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";
import { SHOP_CART_RESUME } from "@/contexts/ShopCartContext";
import { PasswordInput } from "@/components/PasswordInput";
import { restrictedText } from "@/lib/restricted";

const field = { background: "var(--bg-subtle)", border: "1px solid var(--border-color)", color: "var(--text-primary)" };

const SHIP_REQ = ["name", "line1", "city", "zip", "country"];

/* Combined checkout-details step (shop.md Guest Checkout): email-first, inline
   password only if the email is a real password account, shipping folded in when
   the cart has merch, ToS/Refund consent. */
export const CheckoutDetails = ({ hasPhysical, hasDigital, onReady }) => {
  const { platformUser, refresh } = usePlatformAuth();
  const { t, i18n } = useTranslation();
  const loggedIn = !!platformUser;

  const [email, setEmail] = useState("");
  const [needPassword, setNeedPassword] = useState(false);
  const [password, setPassword] = useState("");
  const [ship, setShip] = useState({ name: "", line1: "", line2: "", city: "", state: "", zip: "", country: "" });
  const [agree, setAgree] = useState(false);
  const [dlAgree, setDlAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const setS = (k, v) => setShip((s) => ({ ...s, [k]: v }));

  const shipOk = !hasPhysical || SHIP_REQ.every((k) => ship[k].trim());
  const shippingPayload = hasPhysical ? ship : null;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!agree) { setError(t("checkout.errAgree")); return; }
    if (hasDigital && !dlAgree) { setError(t("checkout.errConsent")); return; }
    if (hasPhysical && !shipOk) { setError(t("checkout.errShipping")); return; }

    let consentId = null;
    // Record the express 14-day-waiver consent for immediate on-platform access.
    // This is the proof we may need if a purchase is later disputed, so it MUST be
    // durable: retry up to 3 times, and if it still cannot be stored, stop the
    // checkout rather than letting the buyer through with no record.
    if (hasDigital) {
      setBusy(true);
      const sentence = t("shop.consent14").replace(/<\/?[tr]>/g, "");
      const consentEmail = (email || (platformUser && platformUser.email) || "").toLowerCase();
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const { data } = await platformClient.post("/shop/consent", { email: consentEmail, consent_text: sentence, locale: i18n.language || "en" });
          consentId = data?.consent_id || null;
          break;
        } catch {
          if (attempt < 3) await new Promise((r) => setTimeout(r, 400 * attempt));
        }
      }
      if (!consentId) { setError(t("checkout.errConsentSave")); setBusy(false); return; }
    }

    if (loggedIn) { setBusy(false); onReady({ email: null, shipping: shippingPayload, consentId }); return; }
    setBusy(true);
    try {
      const payload = needPassword ? { email: email.toLowerCase(), password } : { email: email.toLowerCase() };
      const { data } = await authClient.post("/checkout-identity", payload, { restrictedInline: true });
      if (data.status === "password_required") {
        setNeedPassword(true);
        setBusy(false);
        setError(needPassword ? t("checkout.errPassword") : "");
        return;
      }
      if (data.status === "authed") {
        if (data.token) setPlatformToken(data.token);
        await refresh();
        onReady({ email: null, shipping: shippingPayload, consentId });
        return;
      }
      // guest: shadow account created at payment time
      onReady({ email: email.toLowerCase(), shipping: shippingPayload, consentId });
    } catch (err) {
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      // Never render a raw backend string to the buyer: map known cases to
      // localised keys, fall back to a localised generic for anything else.
      if (d?.status === "password_required") { setNeedPassword(true); setError(t("checkout.errPassword")); }
      else if (status === 429) setError(t("checkout.errTooMany"));
      else if (d?.reason === "surface_banned") setError(restrictedText(err, t));
      else setError(t("checkout.errContinue"));
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="shop-checkout-details">
      {!loggedIn && (
        <>
          <div className="relative">
            <Mail size={15} style={{ position: "absolute", left: 12, top: 11, color: "var(--text-secondary)" }} />
            <input type="email" value={email} onChange={(e) => { setEmail(e.target.value); setNeedPassword(false); setPassword(""); }} placeholder={t("checkout.emailPlaceholder")} required data-testid="checkout-email" className="w-full pl-9 pr-3 py-2.5 rounded-lg text-sm outline-none" style={field} />
          </div>
          {needPassword && (
            <div data-testid="checkout-password-wrap">
              <p className="text-xs mb-1" style={{ color: "var(--text-secondary)" }}>{t("checkout.hasAccount")}</p>
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t("checkout.passwordPlaceholder")} required autoFocus data-testid="checkout-password" className="w-full px-3 py-2.5 rounded-lg text-sm outline-none" style={field} />
            </div>
          )}
        </>
      )}

      {hasPhysical && (
        <div className="space-y-2 pt-1" data-testid="checkout-shipping">
          <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{t("checkout.shipPrompt")}</p>
          <input value={ship.name} onChange={(e) => setS("name", e.target.value)} placeholder={t("checkout.shipName")} data-testid="ship-name" className="w-full px-3 py-2 rounded-lg text-sm outline-none" style={field} />
          <input value={ship.line1} onChange={(e) => setS("line1", e.target.value)} placeholder={t("checkout.shipLine1")} data-testid="ship-line1" className="w-full px-3 py-2 rounded-lg text-sm outline-none" style={field} />
          <input value={ship.line2} onChange={(e) => setS("line2", e.target.value)} placeholder={t("checkout.shipLine2")} data-testid="ship-line2" className="w-full px-3 py-2 rounded-lg text-sm outline-none" style={field} />
          <div className="flex gap-2">
            <input value={ship.city} onChange={(e) => setS("city", e.target.value)} placeholder={t("checkout.shipCity")} data-testid="ship-city" className="w-full px-3 py-2 rounded-lg text-sm outline-none" style={field} />
            <input value={ship.state} onChange={(e) => setS("state", e.target.value)} placeholder={t("checkout.shipState")} data-testid="ship-state" className="w-32 px-3 py-2 rounded-lg text-sm outline-none" style={field} />
          </div>
          <div className="flex gap-2">
            <input value={ship.zip} onChange={(e) => setS("zip", e.target.value)} placeholder={t("checkout.shipZip")} data-testid="ship-zip" className="w-full px-3 py-2 rounded-lg text-sm outline-none" style={field} />
            <input value={ship.country} onChange={(e) => setS("country", e.target.value.toUpperCase())} placeholder={t("checkout.shipCountry")} maxLength={2} data-testid="ship-country" className="w-40 px-3 py-2 rounded-lg text-sm outline-none" style={field} />
          </div>
        </div>
      )}

      <label className="flex items-start gap-2 text-xs cursor-pointer pt-1" style={{ color: "var(--text-secondary)" }}>
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} data-testid="checkout-agree" className="mt-0.5" />
        <span>
          <Trans i18nKey="checkout.terms"
            components={{
              t: <a href="/terms" target="_blank" rel="noreferrer" className="underline" />,
              r: <a href="/refund" target="_blank" rel="noreferrer" className="underline" />,
            }} />
        </span>
      </label>

      {hasDigital && (
        <label className="flex items-start gap-2 text-xs cursor-pointer pt-1" style={{ color: "var(--text-secondary)" }} data-testid="checkout-download-consent-label">
          <input type="checkbox" checked={dlAgree} onChange={(e) => setDlAgree(e.target.checked)} data-testid="checkout-download-consent" className="mt-0.5" />
          <span>
            <Trans i18nKey="shop.consent14"
              components={{
                t: <a href="/terms" target="_blank" rel="noreferrer" className="underline" />,
                r: <a href="/refund" target="_blank" rel="noreferrer" className="underline" />,
              }} />
          </span>
        </label>
      )}

      {error && <p className="text-sm" style={{ color: "#ef4444" }} data-testid="checkout-details-error">{error}</p>}

      <button type="submit" disabled={busy || !agree || (hasDigital && !dlAgree)} data-testid="checkout-continue" className="w-full py-3 rounded-full text-sm font-semibold flex items-center justify-center gap-2" style={{ background: "var(--accent-primary)", color: "#fff", opacity: (busy || !agree || (hasDigital && !dlAgree)) ? 0.6 : 1 }}>
        {busy ? <><Loader2 size={16} className="animate-spin" /> {t("checkout.checking")}</> : <>{t("checkout.continue")}</>}
      </button>
    </form>
  );
};

export default CheckoutDetails;
